/**
 * Life4Earth V2 — Pre-Flight Template Seed Data
 *
 * Reusable templates that can be applied to projects.
 * Applying a template copies items into the project — no live binding.
 * Teams can then modify, add, or remove items freely.
 */

import type { PreflightTemplate } from './preflightTypes';

const PLATFORM_CREATOR = '00000000-0000-0000-0000-000000000000';
const NOW = '2026-01-01T00:00:00Z';

export const preflightTemplateSeedData: PreflightTemplate[] = [
  /* ---------------------------------------------------------------- */
  /*  Template 1: Reforestation / Tree Planting                        */
  /* ---------------------------------------------------------------- */
  {
    template_guid: 'a1b2c3d4-0001-4000-8000-000000000001',
    name: 'Reforestation Project',
    description: 'Standard checklist for tree planting and reforestation initiatives.',
    scope: 'platform',
    community_guid: null,
    items: [
      /* ── Preparation ── */
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Certified arborist or forestry lead',
        description: 'At least one team member with arborist certification or forestry expertise.',
        requirement_type: 'threshold',
        target_quantity: 1,
        unit: 'people',
        visibility: 'public',
        mission_subkpi_key: null,
        tags: ['certification', 'leadership'],
        sort_order: 1,
      },
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Volunteers',
        description: 'Minimum volunteer count to execute the planting phase.',
        requirement_type: 'threshold',
        target_quantity: 5,
        unit: 'people',
        visibility: 'public',
        mission_subkpi_key: 'volunteers',
        tags: ['recruitment'],
        sort_order: 2,
      },
      {
        phase: 'preparation',
        category: 'materials',
        label: 'Native seedlings',
        description: 'Sourced from approved nursery; species appropriate for the site.',
        requirement_type: 'threshold',
        target_quantity: 50,
        unit: 'seedlings',
        visibility: 'public',
        mission_subkpi_key: 'trees_planted',
        tags: ['native-species'],
        sort_order: 3,
      },
      {
        phase: 'preparation',
        category: 'equipment',
        label: 'Planting tools',
        description: 'Shovels, post-hole diggers, protective gloves.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['hand-tools'],
        sort_order: 4,
      },
      {
        phase: 'preparation',
        category: 'financial',
        label: 'Phase 1 funding',
        description: 'Budget for seedlings, supplies, and site preparation.',
        requirement_type: 'threshold',
        target_quantity: 2500,
        unit: 'USD',
        visibility: 'public',
        tags: ['budget'],
        sort_order: 5,
      },
      {
        phase: 'preparation',
        category: 'admin_legal',
        label: 'Land-use permit',
        description: 'Municipal or landowner approval for planting on designated parcels.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['permit'],
        sort_order: 6,
      },
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Soil assessment',
        description: 'Soil pH, drainage, and contamination tests.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['testing', 'soil'],
        sort_order: 7,
      },
      {
        phase: 'preparation',
        category: 'facilities',
        label: 'Site selection finalized',
        description: 'All planting sites surveyed and confirmed.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['site-survey'],
        sort_order: 8,
      },
      /* ── Operational ── */
      {
        phase: 'operational',
        category: 'materials',
        label: 'Mulch and soil amendments',
        description: 'Organic mulch and compost for newly planted areas.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['materials', 'planting'],
        sort_order: 9,
      },
      {
        phase: 'operational',
        category: 'knowledge',
        label: 'Pest management support',
        description: 'Access to pest/disease identification and treatment expertise.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['pest-management'],
        sort_order: 10,
      },
      /* ── Sustainment ── */
      {
        phase: 'sustainment',
        category: 'knowledge',
        label: 'Year 1–3 watering crew',
        description: 'Dedicated team for regular watering during establishment period.',
        requirement_type: 'threshold',
        target_quantity: 2,
        unit: 'people',
        visibility: 'public',
        tags: ['maintenance', 'watering'],
        sort_order: 11,
      },
      {
        phase: 'sustainment',
        category: 'materials',
        label: 'Replacement seedlings (10%)',
        description: 'Reserve stock to replace trees that fail to establish.',
        requirement_type: 'threshold',
        target_quantity: 5,
        unit: 'seedlings',
        visibility: 'internal',
        tags: ['replacement', 'contingency'],
        sort_order: 12,
      },
      {
        phase: 'sustainment',
        category: 'knowledge',
        label: 'Annual health assessment protocol',
        description: 'Documented process for yearly tree survival and growth monitoring.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['monitoring', 'assessment'],
        sort_order: 13,
      },
    ],
    created_by: PLATFORM_CREATOR,
    created_at: NOW,
    updated_at: NOW,
  },

  /* ---------------------------------------------------------------- */
  /*  Template 2: Community Garden                                     */
  /* ---------------------------------------------------------------- */
  {
    template_guid: 'a1b2c3d4-0002-4000-8000-000000000002',
    name: 'Community Garden',
    description: 'Checklist for establishing and maintaining a community garden.',
    scope: 'platform',
    community_guid: null,
    items: [
      /* ── Preparation ── */
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Garden coordinator',
        description: 'A lead person to organize schedules and manage volunteers.',
        requirement_type: 'threshold',
        target_quantity: 1,
        unit: 'people',
        visibility: 'public',
        tags: ['leadership'],
        sort_order: 1,
      },
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Regular gardeners',
        description: 'Committed community members for ongoing maintenance.',
        requirement_type: 'threshold',
        target_quantity: 8,
        unit: 'people',
        visibility: 'public',
        tags: ['volunteers'],
        sort_order: 2,
      },
      {
        phase: 'preparation',
        category: 'facilities',
        label: 'Garden plot secured',
        description: 'Access to land with lease or agreement from property owner.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['land', 'lease'],
        sort_order: 3,
      },
      {
        phase: 'preparation',
        category: 'materials',
        label: 'Raised bed materials',
        description: 'Lumber, soil, and hardware for constructing raised beds.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['construction'],
        sort_order: 4,
      },
      {
        phase: 'preparation',
        category: 'materials',
        label: 'Seeds and starter plants',
        description: 'Seasonal seeds and starter plants for the first growing cycle.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['seeds', 'plants'],
        sort_order: 5,
      },
      {
        phase: 'preparation',
        category: 'equipment',
        label: 'Garden tools',
        description: 'Shovels, rakes, hoes, wheelbarrow, hoses, and watering cans.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['hand-tools'],
        sort_order: 6,
      },
      {
        phase: 'preparation',
        category: 'financial',
        label: 'Startup funds',
        description: 'Budget for materials, water hookup, and tools.',
        requirement_type: 'threshold',
        target_quantity: 1000,
        unit: 'USD',
        visibility: 'public',
        tags: ['budget'],
        sort_order: 7,
      },
      {
        phase: 'preparation',
        category: 'admin_legal',
        label: 'Water access permit',
        description: 'Agreement for water supply hookup to the garden site.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['permit', 'water'],
        sort_order: 8,
      },
      /* ── Operational ── */
      {
        phase: 'operational',
        category: 'materials',
        label: 'Seasonal seed rotation supplies',
        description: 'Seeds and amendments for each growing season.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['seeds', 'seasonal'],
        sort_order: 9,
      },
      {
        phase: 'operational',
        category: 'knowledge',
        label: 'Pest and disease guide',
        description: 'Reference material for identifying and treating common garden pests.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['pest-management', 'reference'],
        sort_order: 10,
      },
      /* ── Sustainment ── */
      {
        phase: 'sustainment',
        category: 'financial',
        label: 'Annual maintenance budget',
        description: 'Recurring funds for soil replenishment, tool replacement, and water.',
        requirement_type: 'threshold',
        target_quantity: 500,
        unit: 'USD',
        visibility: 'public',
        tags: ['budget', 'recurring'],
        sort_order: 11,
      },
      {
        phase: 'sustainment',
        category: 'admin_legal',
        label: 'Lease renewal tracking',
        description: 'Process for timely renewal of the land-use agreement.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['lease', 'renewal'],
        sort_order: 12,
      },
    ],
    created_by: PLATFORM_CREATOR,
    created_at: NOW,
    updated_at: NOW,
  },

  /* ---------------------------------------------------------------- */
  /*  Template 3: Environmental Monitoring                             */
  /* ---------------------------------------------------------------- */
  {
    template_guid: 'a1b2c3d4-0003-4000-8000-000000000003',
    name: 'Environmental Monitoring',
    description: 'Checklist for establishing a long-term environmental monitoring program.',
    scope: 'platform',
    community_guid: null,
    items: [
      /* ── Preparation ── */
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Trained data collectors',
        description: 'Individuals trained in the monitoring protocol.',
        requirement_type: 'threshold',
        target_quantity: 3,
        unit: 'people',
        visibility: 'public',
        tags: ['training', 'data-collection'],
        sort_order: 1,
      },
      {
        phase: 'preparation',
        category: 'knowledge',
        label: 'Monitoring protocol documented',
        description: 'Written protocol covering data collection methods, frequency, and quality standards.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['protocol', 'documentation'],
        sort_order: 2,
      },
      {
        phase: 'preparation',
        category: 'equipment',
        label: 'Monitoring equipment',
        description: 'Sensors, testing kits, GPS, data loggers as required by the protocol.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['sensors', 'instruments'],
        sort_order: 3,
      },
      {
        phase: 'preparation',
        category: 'admin_legal',
        label: 'Site access agreement',
        description: 'Permission from landowners or authorities to access monitoring sites.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['access', 'permit'],
        sort_order: 4,
      },
      {
        phase: 'preparation',
        category: 'financial',
        label: 'Annual operating budget',
        description: 'Funding to cover equipment maintenance, travel, and data management.',
        requirement_type: 'threshold',
        target_quantity: 3000,
        unit: 'USD',
        visibility: 'public',
        tags: ['budget', 'recurring'],
        sort_order: 5,
      },
      /* ── Operational ── */
      {
        phase: 'operational',
        category: 'materials',
        label: 'Calibration supplies',
        description: 'Calibration standards and replacement parts for sensors.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['calibration', 'equipment'],
        sort_order: 6,
      },
      /* ── Sustainment ── */
      {
        phase: 'sustainment',
        category: 'knowledge',
        label: 'Data archival and sharing plan',
        description: 'Long-term strategy for storing and publishing collected data.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'public',
        tags: ['data', 'archival'],
        sort_order: 7,
      },
      {
        phase: 'sustainment',
        category: 'admin_legal',
        label: 'Access agreement renewal',
        description: 'Schedule for renewing site access permissions before expiry.',
        requirement_type: 'binary',
        target_quantity: null,
        unit: null,
        visibility: 'internal',
        tags: ['permit', 'renewal'],
        sort_order: 8,
      },
    ],
    created_by: PLATFORM_CREATOR,
    created_at: NOW,
    updated_at: NOW,
  },
];
